import { api } from './api';
import { AxiosResponse } from 'axios';

export interface BlockedUser {
  id: number;
  blockedUser: {
    id: number;
    username: string;
    profilePhoto: string | null;
  };
  reason: string;
  notes: string | null;
  blockedAt: string;
}

export interface BlockStatusResponse {
  isBlocked: boolean;
  currentUserBlocked: boolean;
  otherUserBlocked: boolean;
  canMessage: boolean;
}

export interface ReportSubmission {
  reportedId: number;
  notes: string;
}

export interface UserReport {
  id: number;
  reporter: {
    id: number;
    username: string;
    profilePhoto: string | null;
  };
  reported: {
    id: number;
    username: string;
    profilePhoto: string | null;
    suspendedAt: string | null;
  };
  notes: string;
  adminNotes: string | null;
  reviewedBy: number | null;
  reviewer: {
    id: number;
    username: string;
  } | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportFilters {
  page: number;
  limit: number;
  status?: string;
  category?: string;
  reporterId?: number;
  reportedId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface ReportsResponse {
  reports: UserReport[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

export interface ReportStats {
  totalReports: number;
  reviewedReports: number;
  pendingReports: number;
  reportsToday: number;
  reportsThisWeek: number;
  topReportedUsers: { 
    userId: number; 
    username: string; 
    reportCount: number; 
    isSuspended: boolean 
  }[];
  topReporters: { 
    userId: number; 
    username: string; 
    reportCount: number 
  }[];
}

export interface BlockUserRequest {
  reason?: 'HARASSMENT' | 'SPAM' | 'INAPPROPRIATE_CONTENT' | 'FAKE_PROFILE' | 'OTHER';
  notes?: string;
}

class UserManagementService {
  /**
   * Block a user
   */
  async blockUser(userId: number, data: BlockUserRequest = {}): Promise<void> {
    const response: AxiosResponse = await api.post(`/users/${userId}/block`, data);
    return response.data;
  }

  /**
   * Unblock a user
   */
  async unblockUser(userId: number): Promise<void> {
    const response: AxiosResponse = await api.delete(`/users/${userId}/block`);
    return response.data;
  }

  /**
   * Get list of blocked users
   */
  async getBlockedUsers(): Promise<BlockedUser[]> {
    const response: AxiosResponse = await api.get('/users/blocked');
    return response.data.data;
  }

  /**
   * Check block status between current user and another user
   */
  async getBlockStatus(userId: number): Promise<BlockStatusResponse> {
    const response: AxiosResponse = await api.get(`/users/${userId}/block-status`);
    return response.data.data;
  }

  /**
   * Submit a report against a user
   */
  async reportUser(reportData: ReportSubmission): Promise<void> {
    const response: AxiosResponse = await api.post('/reports', reportData);
    return response.data;
  }

  /**
   * Get reports (admin only)
   */
  async getReports(filters: ReportFilters): Promise<ReportsResponse> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.reporterId) params.append('reporterId', filters.reporterId.toString());
    if (filters.reportedId) params.append('reportedId', filters.reportedId.toString());
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    params.append('page', filters.page.toString());
    params.append('limit', filters.limit.toString());
    
    const response: AxiosResponse = await api.get(`/reports?${params.toString()}`);
    return {
      reports: response.data.data,
      pagination: response.data.pagination
    };
  }

  /**
   * Update report status (admin only)
   */
  async updateReportStatus(reportId: number, status: string, notes?: string): Promise<void> {
    const response: AxiosResponse = await api.put(`/reports/${reportId}/status`, {
      status,
      notes
    });
    return response.data;
  }

  /**
   * Get report statistics (admin only)
   */
  async getReportStats(): Promise<ReportStats> {
    const response: AxiosResponse = await api.get('/reports/stats');
    return response.data.data;
  }

  /**
   * Suspend user account (admin only)
   */
  async suspendUser(userId: number, reason?: string): Promise<void> {
    const response: AxiosResponse = await api.post(`/reports/users/${userId}/suspend`, {
      reason
    });
    return response.data;
  }

  /**
   * Unsuspend user account (admin only)
   */
  async unsuspendUser(userId: number): Promise<void> {
    const response: AxiosResponse = await api.post(`/reports/users/${userId}/unsuspend`);
    return response.data;
  }
}

// Create and export service instance
export const userManagementService = new UserManagementService();
export default userManagementService;